import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { db } from "../js/firebase";
import {
  doc,
  getDoc,
} from "firebase/firestore";
import { logger } from '../services/loggerService';
import { companyCache } from '../services/companyCache';
import { getUserCompanyIds } from '@/utils/companyAccess';

export const useClientCompaniesStore = defineStore("clientCompanies", () => {
  // State
  const companies = ref([]);
  const currentCompany = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const initialized = ref(false);

  // Init function to get user's companies
  // targetCompanyId: only this company becomes currentCompany (avoids race condition with multi-company users)
  const init = async (userId, user = null, targetCompanyId = null) => {
    if (!userId) {
      logger.warn('[ClientCompanies] Initialization attempted without user ID');
      return;
    }

    // Check if already initialized
    if (initialized.value) {
      logger.debug('[ClientCompanies] Store already initialized');
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      logger.info('[ClientCompanies] Initializing store', { userId, targetCompanyId });

      // If user object is provided, use it directly
      let userData = user;

      // Otherwise, get the user document from Firestore
      if (!userData) {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          userData = { id: userDoc.id, ...userDoc.data() };
        }
      }

      if (userData) {
        // Get all company IDs the user has access to
        const companyIds = getUserCompanyIds(userData);

        if (companyIds.length > 0) {
          logger.info('[ClientCompanies] Loading companies for user', {
            userId,
            companyCount: companyIds.length,
            companyIds
          });

          // Load all companies — only the target becomes currentCompany
          await Promise.all(
            companyIds.map(companyId =>
              getCompany(companyId, { setAsCurrent: companyId === targetCompanyId })
            )
          );
        } else {
          logger.info('[ClientCompanies] User has no companies to load', { userId });
        }
      } else {
        logger.warn('[ClientCompanies] User document not found', { userId });
      }

      initialized.value = true;
      logger.info('[ClientCompanies] Store initialization complete');
    } catch (e) {
      logger.error('[ClientCompanies] Failed to initialize store:', {
        error: e.message,
        userId
      });
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  };

  // Get a specific company by ID
  // options.setAsCurrent: when false, loads into companies[] but does NOT set currentCompany (default true)
  const getCompany = async (companyId, { setAsCurrent = true } = {}) => {
    if (!companyId) {
      logger.warn('[ClientCompanies] Company lookup attempted without ID');
      return null;
    }

    // Check cache first
    const cachedCompany = companyCache.getCompany(companyId);
    if (cachedCompany) {
      // Only set as current company if requested
      if (setAsCurrent) {
        currentCompany.value = cachedCompany;
      }
      // Add to companies array if not already there
      if (!companies.value.find(c => c.id === companyId)) {
        companies.value = [...companies.value, cachedCompany];
      }

      logger.info('[ClientCompanies] Using cached company data', {
        companyId,
        companyName: cachedCompany.name,
        hasLogoUrl: !!cachedCompany.logoUrl,
        setAsCurrent
      });

      // Still fetch fresh data in the background to update cache
      fetchCompanyInBackground(companyId);
      return cachedCompany;
    }

    // No cache, fetch fresh data with loading state
    loading.value = true;
    error.value = null;

    try {
      logger.info('[ClientCompanies] Loading company data from server', { companyId });
      const companyRef = doc(db, "companies", companyId);
      const companyDoc = await getDoc(companyRef);

      if (companyDoc.exists()) {
        const companyData = {
          id: companyDoc.id,
          ...companyDoc.data(),
        };

        // Cache the company data
        companyCache.setCompany(companyId, companyData);

        // Only set as current company if requested
        if (setAsCurrent) {
          currentCompany.value = companyData;
        }
        // Add to companies array if not already there
        if (!companies.value.find(c => c.id === companyId)) {
          companies.value = [...companies.value, companyData];
        }

        logger.info('[ClientCompanies] Company data loaded and cached', {
          companyId,
          hasLogoUrl: !!companyData.logoUrl,
          logoUrl: companyData.logoUrl,
          companyName: companyData.name,
          setAsCurrent,
          allFields: Object.keys(companyData)
        });

        return companyData;
      } else {
        logger.error('[ClientCompanies] Company not found', { companyId });
        error.value = 'Company not found';
        if (setAsCurrent) {
          currentCompany.value = null;
        }
        return null;
      }
    } catch (e) {
      logger.error('[ClientCompanies] Failed to get company:', {
        error: e.message,
        companyId
      });
      error.value = e.message;
      throw e;
    } finally {
      loading.value = false;
    }
  };

  // Fetch company data in background without loading state
  const fetchCompanyInBackground = async (companyId) => {
    try {
      logger.debug('[ClientCompanies] Fetching fresh company data in background', { companyId });
      const companyRef = doc(db, "companies", companyId);
      const companyDoc = await getDoc(companyRef);

      if (companyDoc.exists()) {
        const companyData = {
          id: companyDoc.id,
          ...companyDoc.data(),
        };
        
        // Update cache with fresh data
        companyCache.setCompany(companyId, companyData);
        
        // Update current company if it's the same one
        if (currentCompany.value?.id === companyId) {
          currentCompany.value = companyData;
          // Update in companies array
          const index = companies.value.findIndex(c => c.id === companyId);
          if (index !== -1) {
            companies.value[index] = companyData;
          }
        }
        
        logger.debug('[ClientCompanies] Background company data updated', { 
          companyId,
          companyName: companyData.name
        });
      }
    } catch (e) {
      logger.debug('[ClientCompanies] Background fetch failed (non-critical):', {
        error: e.message,
        companyId
      });
    }
  };

  // Get company by ID (computed getter for consistency)
  const getCompanyById = computed(() => (id) => {
    return companies.value.find(company => company.id === id) || null;
  });

  // Reset store state
  const reset = () => {
    companies.value = [];
    currentCompany.value = null;
    loading.value = false;
    error.value = null;
    initialized.value = false;
    logger.info('[ClientCompanies] Store reset completed');
  };

  return {
    // State
    companies,
    currentCompany,
    loading,
    error,
    initialized,
    
    // Actions
    init,
    getCompany,
    getCompanyById,
    reset,
  };
}); 